import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MobileAppLayout from "@/components/app/MobileAppLayout";
import { useAppMode } from "@/hooks/useAppMode";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

interface FormData {
  emergencyContactName: string;
  emergencyContactPhone: string;
  emergencyContactRelationship: string;
}

const AppEmergencyContact = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { provider } = useAppMode();
  const brandColor = provider?.brand_primary_color || undefined;

  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusMsg, setStatusMsg] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const { register, handleSubmit, reset } = useForm<FormData>();

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      const { data } = await supabase
        .from("profiles")
        .select("emergency_contact_name, emergency_contact_phone, emergency_contact_relationship")
        .eq("id", user.id)
        .maybeSingle();
      reset({
        emergencyContactName: data?.emergency_contact_name || "",
        emergencyContactPhone: data?.emergency_contact_phone || "",
        emergencyContactRelationship: data?.emergency_contact_relationship || "",
      });
      setLoading(false);
    };
    load();
  }, [user, reset]);

  const onSubmit = async (form: FormData) => {
    if (!user) return;
    setStatusMsg(null);
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .upsert({
          id: user.id,
          emergency_contact_name: form.emergencyContactName || null,
          emergency_contact_phone: form.emergencyContactPhone || null,
          emergency_contact_relationship: form.emergencyContactRelationship || null,
          updated_at: new Date().toISOString(),
        });
      if (error) throw error;
      setStatusMsg({ type: "success", text: "Emergency contact updated." });
    } catch (err) {
      setStatusMsg({ type: "error", text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <MobileAppLayout title="Emergency contact">
      <div className="px-4 py-4 space-y-4">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2"
          onClick={() => navigate("/app/profile")}
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>

        <div>
          <h1 className="text-2xl font-bold tracking-tight">Emergency contact</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Who should we contact in case of an emergency during your session?
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="ecName">Full name</Label>
                  <Input id="ecName" {...register("emergencyContactName")} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ecPhone">Phone</Label>
                  <Input
                    id="ecPhone"
                    type="tel"
                    inputMode="tel"
                    {...register("emergencyContactPhone")}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ecRel">Relationship</Label>
                  <Input
                    id="ecRel"
                    placeholder="e.g. Spouse, Parent, Friend"
                    {...register("emergencyContactRelationship")}
                  />
                </div>
              </CardContent>
            </Card>

            {statusMsg && (
              <div
                className={
                  statusMsg.type === "success"
                    ? "rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
                    : "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                }
              >
                {statusMsg.text}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12"
              disabled={saving}
              style={brandColor ? { backgroundColor: brandColor } : undefined}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
            </Button>
          </form>
        )}
      </div>
    </MobileAppLayout>
  );
};

export default AppEmergencyContact;
